import { SafeReportResponse } from "../Conversion";
import { formatAbbreviatedMoney, formatExactMoneyDollars } from "../Money";
import * as client from "../../../client";

export const compareLayers = (a: client.Layer, b: client.Layer): number => {
  // 1. Primary (starting_point=0) before excess
  const aIsPrimary = a.starting_point === 0 ? 0 : 1;
  const bIsPrimary = b.starting_point === 0 ? 0 : 1;
  if (aIsPrimary !== bIsPrimary) return aIsPrimary - bIsPrimary;

  // 2. By starting point ascending
  if (a.starting_point !== b.starting_point)
    return a.starting_point - b.starting_point;

  // 3. By size (range) ascending
  return a.range - b.range;
};

export const sortedLayersFromReport = (
  specialtyPropertyInfo: SafeReportResponse
) => specialtyPropertyInfo.report_json.layer_info.sort(compareLayers);

export const readableLayer = (layer: client.Layer) => {
  const { starting_point, range, participation } = layer;
  const isPartial =
    !!participation && participation > 0 && participation < range;
  const base =
    starting_point === 0
      ? `Primary ${formatAbbreviatedMoney(range)}`
      : `${formatAbbreviatedMoney(range)} xs ${formatAbbreviatedMoney(
          starting_point
        )}`;
  return isPartial
    ? `${formatAbbreviatedMoney(participation)} part of ${base}`
    : base;
};

export const readableLimitOfLiabilityFromLayer = (layer: client.Layer) => {
  const { starting_point, range, participation } = layer;

  const effectiveParticipation = participation ?? range;
  const hasExcess = starting_point > 0;
  const isPartial = effectiveParticipation < range;

  let result = "";

  if (isPartial && hasExcess) {
    // e.g. $10M part of $25M x/s $25M per occurrence
    result = `${formatExactMoneyDollars(effectiveParticipation)} part of ${formatExactMoneyDollars(range)} excess ${formatExactMoneyDollars(starting_point)}`;
  } else if (isPartial) {
    // e.g. $5M part of $10M per occurrence
    result = `${formatExactMoneyDollars(effectiveParticipation)} part of ${formatExactMoneyDollars(range)}`;
  } else if (hasExcess) {
    // e.g. $5M x/s $5M per occurrence
    result = `${formatExactMoneyDollars(range)} excess ${formatExactMoneyDollars(starting_point)}`;
  } else {
    // e.g. $10M per occurrence
    result = formatExactMoneyDollars(range);
  }

  return `${result} per occurrence`;
};

export function hasOverlappingLayers(layers: client.Layer[]): boolean {
  return layers.some((a, i) =>
    layers.slice(i + 1).some((b) => {
      const aEnd = a.starting_point + a.range;
      const bEnd = b.starting_point + b.range;
      return a.starting_point < bEnd && aEnd > b.starting_point;
    })
  );
}
